import React, { memo, Component } from 'react';
import { ChevronLeft, Share, MoreHorizontal, MapPin, Calendar, Search, Menu } from 'lucide-react';
// Reusable iPhone Frame Component
function IPhoneFrame({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl ${className}`} data-id="element-10">
      <div className="w-[148px] h-[18px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute z-20" data-id="element-11"></div>
      <div className="h-[32px] w-[3px] bg-gray-800 absolute -left-[17px] top-[72px] rounded-l-lg" data-id="element-12"></div>
      <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[124px] rounded-l-lg" data-id="element-13"></div>
      <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[178px] rounded-l-lg" data-id="element-14"></div>
      <div className="h-[64px] w-[3px] bg-gray-800 absolute -right-[17px] top-[142px] rounded-r-lg" data-id="element-15"></div>
      <div className="rounded-[2rem] overflow-hidden w-full h-full bg-[#FAF8F5] relative" data-id="element-16">
        {/* Status Bar Mockup */}
        <div className="absolute top-0 w-full h-12 px-6 flex justify-between items-center z-10 text-[10px] font-medium text-[#3A3A3A]" data-id="element-17">
          <span data-id="element-18">9:41</span>
          <div className="flex gap-1" data-id="element-19">
            <div className="w-4 h-2.5 bg-[#3A3A3A] rounded-[1px]" data-id="element-20"></div>
            <div className="w-0.5 h-2.5 bg-[#3A3A3A]/30 rounded-[1px]" data-id="element-21"></div>
          </div>
        </div>
        {children}
        {/* Home Indicator */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 bg-[#3A3A3A]/20 rounded-full z-20" data-id="element-22"></div>
      </div>
    </div>;
}
// Screen 1: Gallery View
function GalleryScreen() {
  const cards = [{
    name: 'The Johnsons',
    year: '2023',
    color: 'bg-[#C4887C]'
  }, {
    name: 'The Smiths',
    year: '2023',
    color: 'bg-[#2D4A3E]'
  }, {
    name: 'Grandma & Grandpa',
    year: '2023',
    color: 'bg-[#B8A99A]'
  }, {
    name: 'The Millers',
    year: '2023',
    color: 'bg-[#3A3A3A]'
  }, {
    name: 'Aunt Sarah',
    year: '2022',
    color: 'bg-[#C4887C]/80'
  }, {
    name: 'The Wilsons',
    year: '2022',
    color: 'bg-[#2D4A3E]/80'
  }];
  return <div className="h-full w-full pt-12 flex flex-col" data-id="element-23">
      {/* Header */}
      <div className="px-5 pb-4 pt-2 flex justify-between items-center border-b border-[#B8A99A]/10" data-id="element-24">
        <h2 className="font-['DM_Sans'] text-xl font-bold text-[#3A3A3A]" data-id="element-25">
          My Cards
        </h2>
        <div className="flex gap-4" data-id="element-26">
          <Search className="w-5 h-5 text-[#3A3A3A]" data-id="element-27" />
          <Menu className="w-5 h-5 text-[#3A3A3A]" data-id="element-28" />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-5 py-4 flex gap-3 overflow-x-auto no-scrollbar" data-id="element-29">
        <span className="px-4 py-1.5 bg-[#2D4A3E] text-[#FAF8F5] text-xs font-medium rounded-full whitespace-nowrap" data-id="element-30">
          All Cards
        </span>
        <span className="px-4 py-1.5 bg-[#F0EBE3] text-[#3A3A3A] text-xs font-medium rounded-full whitespace-nowrap" data-id="element-31">
          Favorites
        </span>
        <span className="px-4 py-1.5 bg-[#F0EBE3] text-[#3A3A3A] text-xs font-medium rounded-full whitespace-nowrap" data-id="element-32">
          2023
        </span>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-5 pb-8" data-id="element-33">
        <div className="grid grid-cols-2 gap-4" data-id="element-34">
          {cards.map((card, i) => <div key={i} className="flex flex-col gap-2" data-id="element-35">
              <div className={`aspect-[4/5] rounded-xl shadow-sm ${card.color} relative overflow-hidden group`} data-id="element-36">
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-37"></div>
                {/* Simulated photo content */}
                <div className="absolute inset-4 border border-white/20 rounded-lg" data-id="element-38"></div>
              </div>
              <div data-id="element-39">
                <p className="text-xs font-bold text-[#3A3A3A]" data-id="element-40">{card.name}</p>
                <p className="text-[10px] text-[#B8A99A]" data-id="element-41">{card.year}</p>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}
// Screen 2: Detail View
function DetailScreen() {
  return <div className="h-full w-full pt-12 flex flex-col bg-[#FAF8F5]" data-id="element-42">
      {/* Header */}
      <div className="px-5 pb-4 pt-2 flex justify-between items-center" data-id="element-43">
        <ChevronLeft className="w-6 h-6 text-[#3A3A3A]" data-id="element-44" />
        <div className="flex gap-4" data-id="element-45">
          <Share className="w-5 h-5 text-[#3A3A3A]" data-id="element-46" />
          <MoreHorizontal className="w-5 h-5 text-[#3A3A3A]" data-id="element-47" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-8" data-id="element-48">
        {/* Main Card Image */}
        <div className="px-5 mb-6" data-id="element-49">
          <div className="aspect-[4/3] w-full bg-[#C4887C] rounded-2xl shadow-md relative overflow-hidden" data-id="element-50">
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-51">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm" data-id="element-52"></div>
            </div>
            {/* Decorative elements to look like a photo */}
            <div className="absolute bottom-4 left-4 right-4 h-1/3 bg-gradient-to-t from-black/30 to-transparent rounded-b-2xl" data-id="element-53"></div>
          </div>
        </div>

        {/* Info Section */}
        <div className="px-6 space-y-6" data-id="element-54">
          <div data-id="element-55">
            <h1 className="font-['DM_Sans'] text-2xl font-bold text-[#3A3A3A] mb-1" data-id="element-56">
              The Martinez Family
            </h1>
            <div className="flex items-center gap-2 text-[#B8A99A] text-sm font-medium" data-id="element-57">
              <Calendar className="w-4 h-4" data-id="element-58" />
              <span data-id="element-59">December 2023</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-[#B8A99A]/10 space-y-3" data-id="element-60">
            <div className="flex items-start gap-3" data-id="element-61">
              <div className="mt-0.5 p-1.5 bg-[#F0EBE3] rounded-full" data-id="element-62">
                <MapPin className="w-4 h-4 text-[#2D4A3E]" data-id="element-63" />
              </div>
              <div data-id="element-64">
                <p className="text-sm font-medium text-[#3A3A3A]" data-id="element-65">
                  123 Maple Street
                </p>
                <p className="text-sm text-[#3A3A3A]/70" data-id="element-66">Portland, OR 97201</p>
              </div>
            </div>

            <div className="h-px w-full bg-[#F0EBE3]" data-id="element-67"></div>

            <div className="flex items-start gap-3" data-id="element-68">
              <div className="w-1 h-full min-h-[24px] bg-[#2D4A3E] rounded-full ml-2.5" data-id="element-69"></div>
              <p className="text-xs text-[#2D4A3E] italic py-1" data-id="element-70">
                Moved from Seattle in 2022
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-[#B8A99A]/10" data-id="element-71">
            <p className="text-sm text-[#3A3A3A]/80 italic leading-relaxed" data-id="element-72">
              "Wishing you joy and peace this holiday season! We've had a
              wonderful year settling into our new home..."
            </p>
          </div>
        </div>
      </div>
    </div>;
}
export function AppMockupSection() {
  return <section className="w-full bg-[#F0EBE3] px-6 py-16 md:py-24 overflow-hidden" data-id="element-73">
      <div className="max-w-6xl mx-auto" data-id="element-74">
        <div className="text-center mb-16" data-id="element-75">
          <h2 className="font-['DM_Sans'] text-3xl md:text-4xl font-medium text-[#3A3A3A] mb-4" data-id="element-76">
            See your memories come to life
          </h2>
          <p className="text-lg text-[#3A3A3A]/70 max-w-2xl mx-auto" data-id="element-77">
            A beautiful, organized home for the cards you cherish.
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center items-center gap-12 md:gap-16 lg:gap-24" data-id="element-78">
          {/* Phone 1 - Gallery */}
          <div className="transform md:-rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-79">
            <IPhoneFrame className="shadow-2xl shadow-[#3A3A3A]/20" data-id="element-80">
              <GalleryScreen data-id="element-81" />
            </IPhoneFrame>
          </div>

          {/* Phone 2 - Detail */}
          <div className="transform md:translate-y-12 md:rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-82">
            <IPhoneFrame className="shadow-2xl shadow-[#3A3A3A]/20" data-id="element-83">
              <DetailScreen data-id="element-84" />
            </IPhoneFrame>
          </div>
        </div>
      </div>
    </section>;
}